import { useColorModeValue, useTheme } from '@chakra-ui/react';
import { CSSObject } from '@emotion/react';
import { cloneElement } from 'react';

import ChakraComponents from '../ChakraComponents';

const getChakraStyles = (error: boolean) => ({
  container: (provided: CSSObject) => ({
    ...provided,
    width: '100%',
    // border: error ? '1px' : '',
    border: '1px solid',
    borderColor: '#E8E8E8',
    borderRadius: '8px',
    ...(error ? { borderColor: '#E53E3E' } : {}),
  }),
  input: (provided: CSSObject) => ({
    ...provided,
    color: 'inherit',
    lineHeight: 1,
  }),
  // valueContainer: (provided: CSSObject, { selectProps: { size } }: any) => {
  //   const px: any = {
  //     sm: '0.75rem',
  //     md: '1rem',
  //     lg: '1rem',
  //   };

  //   return {
  //     ...provided,
  //     padding: `0.125rem ${px[size]}`,
  //   };
  // },
  loadingMessage: (provided: CSSObject, { selectProps: { size } }: any) => {
    const fontSizes: any = {
      sm: 'sm',
      md: 'sm',
      lg: 'sm',
    };

    const paddings: any = {
      sm: '6px 9px',
      md: '8px 12px',
      lg: '10px 15px',
    };

    return {
      ...provided,
      fontSize: fontSizes[size],
      padding: paddings[size],
    };
  },
  singleValue: (provided: CSSObject, { selectProps: { singleValueStyles } }: any) => {
    return {
      ...provided,
      ...singleValueStyles,
    };
  },
  // Add the chakra style for when a TagCloseButton has focus
  multiValueRemove: (_: CSSObject, { isFocused, selectProps: { multiValueRemoveFocusStyle } }: any) =>
    isFocused ? multiValueRemoveFocusStyle : {},
  multiValue: (provided: CSSObject) => ({
    ...provided,
    background: 'var(--timeero-colors-primary-500)',
    borderRadius: 'var(--timeero-radii-md)',
    color: 'white',
  }),
  multiValueLabel: (provided: CSSObject) => ({ ...provided, fontSize: '13px', color: 'white' }),
  menu: (provided: CSSObject) => ({
    ...provided,
    zIndex: 10000,
  }),
  // menuPortal: (provided: CSSObject) => ({
  //   ...provided,
  //   zIndex: 10000,
  // }),
  menuList: (provided: CSSObject) => {
    return {
      ...provided,
      border: '1px solid var(--timeero-colors-gray-200)',
      bg: '#fff',
      borderRadius: 'md',
      borderWidth: '1px',
      boxShadow: 'sm',
      color: 'inherit',
      minW: '3xs',
      py: '2',
      zIndex: 1,
    };
  },

  // control: () => ({}),
  // option: () => ({}),
  // multiValueLabel: () => ({}),
  // group: () => ({}),
});

const ChakraReactSelect: React.FC<any> = ({
  children,
  styles = {},
  components = {},
  theme = () => ({}),
  size = 'md',
  colorScheme = 'gray',
  ...props
}: any) => {
  const chakraTheme = useTheme();

  const chakraStyles = getChakraStyles(props?.error || false);

  // The chakra theme styles for TagCloseButton when focused
  const closeButtonFocus = chakraTheme.components.Tag.baseStyle.closeButton._focus;
  const multiValueRemoveFocusStyle = {
    background: closeButtonFocus.bg,
    boxShadow: chakraTheme.shadows[closeButtonFocus.boxShadow],
  };

  // The chakra UI global placeholder color
  // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/styles.ts#L13
  const placeholderColor = useColorModeValue(chakraTheme.colors.gray[400], chakraTheme.colors.whiteAlpha[400]);

  // Ensure that the size used is one of the options, either `sm`, `md`, or `lg`
  let realSize = size;
  const sizeOptions = ['sm', 'md', 'lg'];
  if (!sizeOptions.includes(size)) {
    realSize = 'md';
  }

  const select = cloneElement(children, {
    components: {
      ...ChakraComponents,
      ...components,
    },
    styles: {
      ...chakraStyles,
      ...styles,
    },
    theme: (baseTheme: any) => {
      const propTheme = theme(baseTheme);

      return {
        ...baseTheme,
        ...propTheme,
        colors: {
          ...baseTheme.colors,
          neutral50: placeholderColor, // placeholder text color
          neutral40: placeholderColor, // noOptionsMessage color
          ...propTheme.colors,
        },
        spacing: {
          ...baseTheme.spacing,
          ...propTheme.spacing,
        },
      };
    },
    colorScheme,
    size: realSize,
    multiValueRemoveFocusStyle,
    ...props,
  });

  return select;
};

export default ChakraReactSelect;
